import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Search, Send, ShieldCheck, KeyRound, Loader2, Info, CheckCircle2, Clock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useUnreadJoinRequestResponses } from "@/hooks/useUnreadJoinRequestResponses";

interface ShipperResult {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  documents_verified_at: string | null;
  lr_direct_accept_requests: boolean;
  is_member: boolean;
  has_pending_request: boolean;
}

// Normalize to Crockford-ish: uppercase, strip whitespace, common look-alikes
function normalizeId(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/[0OIL1]/g, (c) => (c === "0" || c === "O" ? "0" : c === "1" || c === "I" || c === "L" ? "1" : c));
}

export default function LRDirectShippersPage() {
  usePageMeta({
    title: "LR Direct — Find Shipper | LaneRunner",
    description: "Look up an LR Direct shipper by their private network ID.",
  });

  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const responseCount = useUnreadJoinRequestResponses(user?.id ?? null);

  const [idInput, setIdInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<ShipperResult | null | "not_found">(null);

  const [openRequest, setOpenRequest] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const isProvider =
    profile?.user_type === "broker" ||
    profile?.user_type === "broker_carrier" ||
    profile?.user_type === "carrier";

  const handleSearch = async () => {
    const raw = idInput.trim();
    if (raw.length < 4) {
      toast.error("Enter the full Private Network ID");
      return;
    }
    setSearching(true);
    setResult(null);
    try {
      const { data, error } = await supabase.rpc(
        "find_shipper_by_lr_direct_id" as any,
        { _id: raw }
      );
      if (error) throw error;
      const row = Array.isArray(data) ? (data[0] as any) : null;
      setResult(row ? (row as ShipperResult) : "not_found");
    } catch (e: any) {
      toast.error(e?.message || "Lookup failed");
      setResult(null);
    } finally {
      setSearching(false);
    }
  };

  const submitRequest = async () => {
    if (!result || result === "not_found") return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("request-network-membership", {
        body: { shipperCompanyId: result.id, message: message.trim() || undefined },
      });
      // IMPORTANT: edge function returns its friendly message in `data.error`
      // even when supabase-js raises a FunctionsHttpError. Read body first so
      // users don't see "Edge Function returned a non-2xx status code".
      const bodyError = (data as any)?.error as string | undefined;
      if (bodyError) {
        const lower = bodyError.toLowerCase();
        if (lower.includes("already in this shipper")) {
          toast.success("You're already in this network");
          setOpenRequest(false);
          // refresh result so badge shows
          setResult({ ...result, is_member: true });
          return;
        }
        if (lower.includes("already have a pending request")) {
          toast.message("Request already pending", { description: "The shipper hasn't responded yet." });
          setOpenRequest(false);
          setResult({ ...result, has_pending_request: true });
          return;
        }
        throw new Error(bodyError);
      }
      if (error) throw error;
      toast.success(`Request sent to ${result.name}`);
      setOpenRequest(false);
      setMessage("");
      setResult({ ...result, has_pending_request: true });
    } catch (e: any) {
      toast.error(e?.message || "Failed to send request");
    } finally {
      setSending(false);
    }
  };

  if (!isProvider) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Shipper lookup is available to LR Direct member accounts.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card px-6 py-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate("/lr-direct")}>
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-teal/10">
              <KeyRound className="h-5 w-5 text-brand-teal" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-foreground truncate">Find Shipper</h1>
              <p className="text-sm text-muted-foreground truncate">
                Look up a shipper by their LR Direct Private Network ID.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/lr-direct/network-invitations")}
            className="relative gap-2"
          >
            My Invitations & Requests
            {responseCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-bold">
                {responseCount > 9 ? "9+" : responseCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-6 space-y-5">
        {/* Privacy notice */}
        <Card className="p-4 bg-muted/30 border-dashed">
          <div className="flex gap-3">
            <Info className="h-4 w-4 text-brand-teal shrink-0 mt-0.5" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-foreground">Private by design.</span> LR Direct
              shippers are not publicly listed. Ask the shipper for their 8-character Private Network ID
              (e.g. <code className="px-1 py-0.5 rounded bg-muted font-mono text-[11px]">AB7K9X2M</code>),
              then request to join their network here.
            </div>
          </div>
        </Card>

        {/* Lookup */}
        <Card className="p-5">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
            Private Network ID
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="e.g. AB7K9X2M"
                value={idInput}
                onChange={(e) => setIdInput(normalizeId(e.target.value))}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                className="pl-8 h-10 font-mono tracking-widest uppercase"
                maxLength={12}
                autoFocus
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={searching || idInput.trim().length < 4}
              className="gap-2 text-white h-10 px-5"
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {searching ? "Searching…" : "Look up"}
            </Button>
          </div>
        </Card>

        {/* Result */}
        {result === "not_found" && (
          <Card className="p-8 text-center">
            <Building2 className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-semibold mb-1">No shipper found with that ID</p>
            <p className="text-xs text-muted-foreground">
              Double-check the ID with the shipper. IDs are 8 characters, letters and numbers only.
            </p>
          </Card>
        )}

        {result && result !== "not_found" && (
          <Card className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-muted shrink-0">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-base flex items-center gap-1.5 flex-wrap">
                    {result.name}
                    {result.documents_verified_at && (
                      <ShieldCheck className="h-4 w-4 text-brand-teal shrink-0" />
                    )}
                    {result.is_member && (
                      <Badge className="bg-brand-teal/15 text-brand-teal border-0 text-[10px] uppercase font-bold gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Already a member
                      </Badge>
                    )}
                    {!result.is_member && result.has_pending_request && (
                      <Badge className="bg-amber-500/15 text-amber-600 border-0 text-[10px] uppercase font-bold gap-1">
                        <Clock className="h-3 w-3" /> Request pending
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[result.city, result.state].filter(Boolean).join(", ") || "Location not set"}
                  </div>
                </div>
              </div>
            </div>

            {result.is_member ? (
              <Button disabled variant="outline" className="w-full gap-2">
                <CheckCircle2 className="h-4 w-4" /> You're already in this network
              </Button>
            ) : result.has_pending_request ? (
              <Button disabled variant="outline" className="w-full gap-2">
                <Clock className="h-4 w-4" /> Request pending review
              </Button>
            ) : result.lr_direct_accept_requests ? (
              <Button
                onClick={() => { setMessage(""); setOpenRequest(true); }}
                className="w-full gap-2 text-white"
                style={{ backgroundColor: "hsl(var(--brand-teal))" }}
              >
                <Send className="h-4 w-4" /> Request to join network
              </Button>
            ) : (
              <Button disabled variant="outline" className="w-full">
                Not accepting requests
              </Button>
            )}
          </Card>
        )}
      </div>

      {/* Request dialog */}
      <Dialog open={openRequest} onOpenChange={(o) => !o && setOpenRequest(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Request to join {result && result !== "not_found" ? result.name : ""}
            </DialogTitle>
            <DialogDescription>
              Tell them why your operation is a good fit. They'll review your company before approving.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="We run the I-95 corridor weekly with 12 reefer trucks. Available for dedicated contracts."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            maxLength={500}
            disabled={sending}
          />
          <p className="text-[11px] text-muted-foreground">
            {message.length}/500 characters · You can have up to 10 pending requests at a time.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenRequest(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              onClick={submitRequest}
              disabled={sending}
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
              className="gap-2 text-white"
            >
              <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
